/* eslint-disable max-statements */
import { useCallback, useEffect, useState } from "react";
import { LoaderFunctionArgs, useFetcher, useLoaderData, useNavigate } from "react-router-dom";
import { useGlobal } from "../../components/GlobalProviderLayout";
import { CLOSE_TOAST, OPEN_TOAST, RESET_EMAIL_SUBMITTED } from "../../components/GlobalProviderLayout/GlobalProviderLayoutModel";
import { packObjForSubmitWithAttachments, reformatDate } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { doFetchFormDataPost } from "../../utils/doFetchFormDataPost";
import {
  calculateDaysBetweenDates,
  validateFields
} from "../../utils/paidPersonalLeaveRequestValidationUtil";
import { PaidPersonalLeaveRequest } from "./PaidPersonalLeaveRequest";
import { IEmployeeInfo, IPaidPersonalLeaveRequestData } from "./PaidPersonalLeaveRequestData";
import { defaultData, defaultDisclaimer, IDisclaimer, MAX_ATTACHMENTS, ProgressBarSteps } from "./PaidPersonalLeaveRequestModels";

export const PaidPersonalLeaveRequestRouteLoader = async ({ request }: LoaderFunctionArgs) =>
  doFetch('lrs/employee', { signal: request.signal }, true, false);

export const PaidPersonalLeaveRequestRouteAction = async ({ request }: { request: any, params: any }) => {
  let formData = await request.formData();
  let body = formData;
  return doFetchFormDataPost('lrs/personal-leave', { signal: request.signal, body })
}

export const PaidPersonalLeaveRequestRoute = () => {
  const fetcher = useFetcher();
  const data = useLoaderData() as IPaidPersonalLeaveRequestData;
  const navigate = useNavigate();
  const global = useGlobal();

  const [currentData, setCurrentData] = useState<IPaidPersonalLeaveRequestData>(
    { ...defaultData, ...data, employeeInfo: { ...defaultData.employeeInfo, ...data.employeeInfo } }
  );

  const [activeStep, setActiveStep] = useState<ProgressBarSteps>(ProgressBarSteps.PersonalInfo);
  const [error, setError] = useState<Record<string, JSX.Element | string>>({});
  const [disclaimer, setDisclaimer] = useState<IDisclaimer>(defaultDisclaimer);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
  const [blurredFields, setBlurredFields] = useState<Partial<Record<keyof IEmployeeInfo, boolean>>>({});
  const hasError = Object.keys(error).length > 0;
  const isSubmitting = fetcher.state === "submitting";

  useEffect(() => {
    if (fetcher.data && !fetcher.data?.isSuccess && !global.state.toast.open) {
      global.dispatch({
        type: OPEN_TOAST,
        payload: {
          msg: fetcher.data?.message,
          severity: "error",
          closeTime: 0,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });
    } else if (fetcher.data?.isSuccess) {
      global.dispatch({ type: CLOSE_TOAST });
      navigate("/paid-personal-leave-request-submitted");
    }
  }, [fetcher, navigate]);

  useEffect(() => {
    if (global.state.emailSubmitted) {
      global.dispatch({
        type: OPEN_TOAST,
        payload: {
          msg: "We've sent a verification email to the address you provided. It may take a few minutes to arrive. To continue, refresh this page once your email is verified.",
          closeTime: 0,
          severity: "info",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });

      global.dispatch({ type: RESET_EMAIL_SUBMITTED });
    }
  }, [global]);

  useEffect(() => {
    const fieldErrors = validateFields(currentData.employeeInfo, activeStep);
    setIsNextDisabled(Object.keys(fieldErrors).length > 0);
  }, [currentData, activeStep]);

  const checkIfNextShouldBeDisabled = useCallback(() => {
    const fieldErrors = validateFields(currentData.employeeInfo, activeStep);
    setIsNextDisabled(Object.keys(fieldErrors).length > 0);
  }, [currentData, activeStep]);

  useEffect(() => {
    checkIfNextShouldBeDisabled();
  }, [currentData, activeStep, checkIfNextShouldBeDisabled]);

  const validateAndSet = (
    employeeInfo: IEmployeeInfo,
    activeStep: ProgressBarSteps,
    setError: React.Dispatch<React.SetStateAction<Record<string, JSX.Element | string>>>,
    setIsNextDisabled: React.Dispatch<React.SetStateAction<boolean>>
  ): Record<string, JSX.Element | string> => {
    const errors = validateFields(employeeInfo, activeStep, blurredFields);
    setError(errors);
    setIsNextDisabled(Object.keys(errors).length > 0);
    return errors;
  };

  const onChangeField = <K extends keyof IEmployeeInfo>(label: K, value: IEmployeeInfo[K]) => {
    if (label !== 'requestedDateFrom' && label !== 'requestedDateTo') {
      setBlurredFields(prev => ({ ...prev, [label]: true }));
    }

    setCurrentData((prevData) => {
      if (!prevData) return defaultData;

      const updatedEmployeeInfo = {
        ...prevData.employeeInfo,
        [label]: value
      };

      // Recalculate days when date fields change
      if (label === "requestedDateFrom" || label === "requestedDateTo") {
        updatedEmployeeInfo.numberOfDaysRequested = calculateDaysBetweenDates(
          updatedEmployeeInfo.requestedDateFrom,
          updatedEmployeeInfo.requestedDateTo
        );
      }

      validateAndSet(updatedEmployeeInfo, activeStep, setError, setIsNextDisabled);

      return {
        ...prevData,
        employeeInfo: updatedEmployeeInfo,
      };
    });
  };

  const onBlurField = <K extends keyof IEmployeeInfo>(label: K) => {
    setBlurredFields(prev => ({ ...prev, [label]: true }));
    validateAndSet(currentData.employeeInfo, activeStep, setError, setIsNextDisabled);
  };

  const onValidateNextStep = () => {
    const fieldErrors = validateFields(currentData.employeeInfo, activeStep, blurredFields);
    setError(fieldErrors);
    if (Object.keys(fieldErrors).length > 0 && !global.state.toast.open) {
      global.dispatch({
        type: OPEN_TOAST,
        payload: {
          msg: Object.values(fieldErrors)[0],
          severity: "error",
          closeTime: 0,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          },
        }
      });

      return false;
    }
    return true;
  };

  const onSubmit = () => {
    if (hasError || isSubmitting) return;

    const unformattedPhoneNumber = currentData.employeeInfo.employeePhoneNumber?.replace(/\D+/g, "") || "";

    const formData = packObjForSubmitWithAttachments({
      ...currentData.employeeInfo,
      employeePhoneNumber: unformattedPhoneNumber,
      leaveTypeCode: "PL",
      requestedDateFrom: reformatDate(currentData.employeeInfo.requestedDateFrom),
      requestedDateTo: reformatDate(currentData.employeeInfo.requestedDateTo)
    });

    fetcher.submit(formData, {
      method: "post",
      encType: "multipart/form-data"
    });
  };

  const onBack = () => {
    const currentIndex = Object.values(ProgressBarSteps).indexOf(activeStep);
    if (currentIndex > 0) setActiveStep(Object.values(ProgressBarSteps)[currentIndex - 1]);
  };

  const onNext = () => {
    const currentIndex = Object.values(ProgressBarSteps).indexOf(activeStep);
    if (
      currentIndex < Object.values(ProgressBarSteps).length - 1 &&
      onValidateNextStep()
    ) {
      setActiveStep(Object.values(ProgressBarSteps)[currentIndex + 1]);
    }
  };

  const onDateChange = (field: keyof IEmployeeInfo, value: string) =>
    onChangeField(field, value);

  const onFileChange = (files: any) => {
    setBlurredFields(prev => ({ ...prev, attachments: true }));
    const newFiles = files instanceof FileList ? Array.from(files) : files;

    setCurrentData((prevData) => {
      const existingFiles = prevData.employeeInfo.attachments || [];
      const remainingSlots = MAX_ATTACHMENTS - existingFiles.length;
      if (remainingSlots <= 0) {
        return prevData;
      }
      const allowedNewFiles = newFiles.slice(0, remainingSlots);

      const updatedData = {
        ...prevData,
        employeeInfo: {
          ...prevData.employeeInfo,
          attachments: [...existingFiles, ...allowedNewFiles],
        }
      };

      validateAndSet(updatedData.employeeInfo, activeStep, setError, setIsNextDisabled);
      return updatedData;
    });
  };

  const onRemoveFile = (index: number) => {
    if (!currentData.employeeInfo.attachments) return;
    const dt = new DataTransfer();

    Array.from(currentData.employeeInfo.attachments)
      .filter((_, i) => i !== index)
      .forEach((file) => dt.items.add(file));

    setCurrentData((prevData) => {
      const updatedData = {
        ...prevData,
        employeeInfo: {
          ...prevData.employeeInfo,
          attachments: Array.from(dt.files),
        }
      };

      validateAndSet(updatedData.employeeInfo, activeStep, setError, setIsNextDisabled);
      return updatedData;
    });
  };

  return (
    <PaidPersonalLeaveRequest
      disclaimer={disclaimer}
      onShowDisclaimer={(newDisclaimer: IDisclaimer) => setDisclaimer(newDisclaimer)}
      onBack={onBack}
      onNext={onNext}
      onSubmit={onSubmit}
      onChangeField={onChangeField}
      error={error}
      data={currentData}
      activeStep={activeStep}
      onUpdateEmail={() => navigate("/verify-contact-email/paid-personal-leave-request")}
      isNextDisabled={isNextDisabled}
      isSubmitting={isSubmitting}
      onDateChange={onDateChange}
      onDateBlur={onBlurField}
      onFileChange={onFileChange}
      onRemoveFile={onRemoveFile}
      blurredFields={blurredFields}
    />
  );
};  